import { getRecord, listRecords, saveRecord, deleteRecord, addSyncLog } from '@/services/localDb';
import { getOrCreateDeviceId } from '@/services/localDb/device';
import {
  getLocalProducts,
  getLocalTables,
  hasRestaurantOfflineData,
} from '@/services/offline/restaurantOfflineData';
import {
  createLocalId,
  enqueuePendingAction,
  getPendingActionCount,
  listPendingActions,
} from '@/services/offline/pendingActionQueue';
import { assertCurrentDeviceCanCreateOfflineActions } from '@/services/device/deviceOperationalPolicy';

const ORDER_SYNC_STATUS = 'pending_sync';
const OPEN_SERVICE_STATUSES = ['open_local', 'pending_sync'];
const OPEN_ORDER_STATUSES = ['draft_local', 'confirmed_local', 'pending_sync', 'sync_error'];

function nowIso() {
  return new Date().toISOString();
}

function normalizeId(value) {
  if (value === undefined || value === null) return null;
  return String(value);
}

function normalizeMoney(value) {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? Math.round(numberValue * 100) / 100 : 0;
}

function toPlainJson(value, fallbackValue) {
  try {
    return JSON.parse(JSON.stringify(value ?? fallbackValue));
  } catch {
    return fallbackValue;
  }
}

function normalizeModifiersSnapshot(modifiersSnapshot = []) {
  return toPlainJson(modifiersSnapshot, []);
}

function modifierSupplementTotal(modifiersSnapshot = []) {
  return normalizeModifiersSnapshot(modifiersSnapshot).reduce((total, item) => {
    if (Array.isArray(item?.plates_selected)) {
      return total + item.plates_selected.reduce(
        (stepTotal, stepPlate) => stepTotal + normalizeMoney(stepPlate?.supplement),
        0
      );
    }
    return total + normalizeMoney(item?.supplement);
  }, 0);
}

function stableLineOptionsKey({ notes = '', modifiers_snapshot = [] } = {}) {
  return JSON.stringify({
    notes: notes || '',
    modifiers_snapshot: normalizeModifiersSnapshot(modifiers_snapshot),
  });
}

function assertRestaurantId(restaurantId) {
  if (!restaurantId) {
    throw new Error('No hay restaurante activo para trabajar sin conexion.');
  }
  return String(restaurantId);
}

function assertSameRestaurant(record, restaurantId, entityName) {
  if (!record || String(record.restaurant_id || '') !== String(restaurantId)) {
    throw new Error(`${entityName} no pertenece al restaurante activo.`);
  }
}

function getUserId(context = {}) {
  return normalizeId(context.user_id || context.userId || context.waiter_id || context.waiterId);
}

function getUserName(context = {}) {
  return String(
    context.user_name
      || context.userName
      || context.display_name
      || context.username
      || '',
  ).trim() || null;
}

async function getScopedTable(restaurantId, tableId) {
  const tables = await getLocalTables(restaurantId);
  return tables.find((table) => normalizeId(table.id) === normalizeId(tableId)) || null;
}

async function getScopedProduct(restaurantId, productId) {
  const products = await getLocalProducts(restaurantId);
  return products.find((product) => normalizeId(product.id) === normalizeId(productId)) || null;
}

async function listRestaurantRecords(storeName, restaurantId) {
  const records = await listRecords(storeName, { indexName: 'restaurant_id', query: String(restaurantId) });
  return records.filter((record) => String(record.restaurant_id || '') === String(restaurantId));
}

async function listOrderLines(orderLocalId, restaurantId = null) {
  const records = await listRecords('order_lines', { indexName: 'order_local_id', query: orderLocalId });
  return records.filter((line) =>
    line.status !== 'cancelled_local'
    && (!restaurantId || String(line.restaurant_id || '') === String(restaurantId))
  );
}

async function saveRequiredRecord(storeName, record) {
  assertCurrentDeviceCanCreateOfflineActions();
  const saved = await saveRecord(storeName, record);
  if (!saved) {
    throw new Error('No se pudo guardar el cambio offline en IndexedDB.');
  }
  return saved;
}

function productUnitPrice(product, options = {}) {
  const basePrice = normalizeMoney(product?.price);
  const modifierTotal = modifierSupplementTotal(options.modifiers_snapshot);
  return normalizeMoney(basePrice + modifierTotal);
}

function productName(product) {
  return product?.name || product?.plate_name || 'Producto';
}

function productTaxRate(product) {
  return Number(product?.IVA ?? product?.tax_rate ?? 0) || 0;
}

function lineTotal(quantity, unitPrice) {
  return normalizeMoney(Number(quantity || 0) * Number(unitPrice || 0));
}

function normalizeVisibleKitchenState(value, fallback = 'PENDIENTE') {
  const visibleStates = new Set(['PENDIENTE', 'EN ESPERA', 'EN CURSO', 'EN CAMINO', 'LISTO', 'ENTREGADO', 'CANCELADO', 'CANCELADO POR CLIENTE', 'PAGADO']);
  const normalized = String(value || '').trim().toUpperCase();
  if (visibleStates.has(normalized)) {
    return normalized;
  }
  return fallback;
}

function buildVisibleOrderLine(line, orderKitchenState = null) {
  const visibleState = normalizeVisibleKitchenState(line?.kitchen_state || line?.status, orderKitchenState || 'PENDIENTE');
  return {
    ...line,
    state: visibleState,
    sync_status: line?.sync_status || ORDER_SYNC_STATUS,
  };
}

function buildVisibleLocalOrder(order, lines = []) {
  const visibleLines = lines.map((line) => buildVisibleOrderLine(line, order?.kitchen_state));
  const visibleState = normalizeVisibleKitchenState(
    order?.kitchen_state || visibleLines[0]?.state || order?.status,
    'PENDIENTE'
  );

  return {
    ...order,
    kitchen_state: visibleState,
    state: visibleState,
    lines: visibleLines,
  };
}

function taxFromGross(total, taxRate) {
  const rate = Number(taxRate || 0) / 100;
  if (!rate) return 0;
  return normalizeMoney(total * rate / (1 + rate));
}

async function enqueueOrderAction(restaurantId, action) {
  return enqueuePendingAction({
    restaurant_id: restaurantId,
    ...action,
  });
}

async function findDraftOrderForService(restaurantId, serviceLocalId) {
  const orders = await listRestaurantRecords('orders', restaurantId);
  return orders.find((order) =>
    order.service_local_id === serviceLocalId && order.status === 'draft_local'
  ) || null;
}

export async function closeLocalService(restaurantId, serviceLocalId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const service = await getRecord('open_services', serviceLocalId);
  assertSameRestaurant(service, scopedRestaurantId, 'El servicio local');

  if (!OPEN_SERVICE_STATUSES.includes(service.status)) {
    return service;
  }

  const timestamp = nowIso();
  const nextService = {
    ...service,
    status: 'closed_local',
    sync_status: ORDER_SYNC_STATUS,
    closed_at_local: timestamp,
    updated_at_local: timestamp,
  };
  await saveRequiredRecord('open_services', nextService);

  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'service',
    entity_local_id: nextService.local_id,
    entity_server_id: nextService.server_id || null,
    action_type: 'close_service',
    payload: {
      ...nextService,
      closed_at_local: timestamp,
    },
  });

  const table = await getRecord('tables', nextService.table_id);
  if (table && String(table.restaurant_id || '') === scopedRestaurantId) {
    await saveRequiredRecord('tables', {
      ...table,
      local_occupied: false,
      local_service_id: null,
      start_time: null,
      updated_at: timestamp,
    });
  }

  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'table',
    entity_local_id: nextService.table_id,
    action_type: 'mark_table_free',
    payload: {
      table_id: nextService.table_id,
      service_local_id: nextService.local_id,
      local_only: true,
      updated_at_local: timestamp,
    },
  });

  return nextService;
}

export async function canCreateOfflineOrder(restaurantId) {
  const availability = await getOfflineOrderingAvailability(restaurantId);
  return availability.available;
}

export async function getOfflineOrderingAvailability(restaurantId) {
  try {
    assertCurrentDeviceCanCreateOfflineActions();
  } catch (error) {
    return { available: false, reason: error.message, code: error.code };
  }
  if (!restaurantId) {
    return {
      available: false,
      reason: 'No hay restaurante activo.',
    };
  }

  const scopedRestaurantId = String(restaurantId);
  const [hasCache, products, tables] = await Promise.all([
    hasRestaurantOfflineData(scopedRestaurantId),
    getLocalProducts(scopedRestaurantId),
    getLocalTables(scopedRestaurantId),
  ]);

  if (!hasCache || !products.length || !tables.length) {
    return {
      available: false,
      reason: 'Este dispositivo no tiene datos offline de este restaurante. Conectate a internet y actualiza los datos offline antes de trabajar sin conexion.',
    };
  }

  return {
    available: true,
    reason: '',
    products_count: products.length,
    tables_count: tables.length,
  };
}

export async function getOrCreateLocalServiceForTable(restaurantId, tableId, context = {}) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const scopedTableId = normalizeId(tableId);
  const table = await getScopedTable(scopedRestaurantId, scopedTableId);
  if (!table) {
    throw new Error('La mesa no existe en la cache offline del restaurante activo.');
  }

  const services = await listRestaurantRecords('open_services', scopedRestaurantId);
  const existing = services.find((service) =>
    normalizeId(service.table_id) === scopedTableId && OPEN_SERVICE_STATUSES.includes(service.status)
  );
  if (existing) return existing;

  const timestamp = nowIso();
  const deviceId = getOrCreateDeviceId();
  const service = {
    local_id: createLocalId('local_service'),
    server_id: null,
    restaurant_id: scopedRestaurantId,
    table_id: scopedTableId,
    waiter_id: normalizeId(context.waiter_id || context.waiterId),
    status: 'open_local',
    sync_status: ORDER_SYNC_STATUS,
    created_at_local: timestamp,
    updated_at_local: timestamp,
    created_by_device_id: deviceId,
    created_by_user_id: getUserId(context),
    created_by_name: getUserName(context),
  };

  await saveRequiredRecord('open_services', service);
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: deviceId,
    entity_type: 'service',
    entity_local_id: service.local_id,
    action_type: 'create_service',
    payload: service,
  });
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: deviceId,
    entity_type: 'table',
    entity_local_id: scopedTableId,
    action_type: 'mark_table_occupied',
    payload: {
      table_id: scopedTableId,
      service_local_id: service.local_id,
      local_only: true,
    },
  });

  await saveRequiredRecord('tables', {
    ...table,
    local_occupied: true,
    local_service_id: service.local_id,
    start_time: table.start_time || timestamp,
    updated_at: timestamp,
  });

  return service;
}

export async function createLocalOrder(restaurantId, serviceLocalId, context = {}) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const service = await getRecord('open_services', serviceLocalId);
  assertSameRestaurant(service, scopedRestaurantId, 'El servicio local');

  const timestamp = nowIso();
  const deviceId = getOrCreateDeviceId();
  const order = {
    local_id: createLocalId('local_order'),
    server_id: null,
    restaurant_id: scopedRestaurantId,
    service_local_id: service.local_id,
    service_server_id: null,
    table_id: service.table_id,
    waiter_id: service.waiter_id || normalizeId(context.waiter_id || context.waiterId),
    status: 'draft_local',
    sync_status: ORDER_SYNC_STATUS,
    kitchen_state: 'PENDIENTE',
    total_amount: 0,
    tax_amount: 0,
    currency: context.currency || 'EUR',
    created_at_local: timestamp,
    updated_at_local: timestamp,
    created_by_device_id: deviceId,
    created_by_user_id: getUserId(context) || service.created_by_user_id || null,
    created_by_name: getUserName(context) || service.created_by_name || null,
    source: 'offline',
  };

  await saveRequiredRecord('orders', order);
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: deviceId,
    entity_type: 'order',
    entity_local_id: order.local_id,
    action_type: 'create_order',
    payload: order,
  });

  return order;
}

export async function getOrCreateLocalDraftOrderForTable(restaurantId, tableId, context = {}) {
  const service = await getOrCreateLocalServiceForTable(restaurantId, tableId, context);
  const draftOrder = await findDraftOrderForService(String(restaurantId), service.local_id);
  return draftOrder || createLocalOrder(restaurantId, service.local_id, context);
}

export async function addLocalOrderLine(restaurantId, orderLocalId, productId, quantity = 1, options = {}) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const order = await getRecord('orders', orderLocalId);
  assertSameRestaurant(order, scopedRestaurantId, 'El pedido local');

  const product = await getScopedProduct(scopedRestaurantId, productId);
  if (!product) {
    throw new Error('El producto no existe en la cache offline del restaurante activo.');
  }

  const amount = Number(quantity || 0);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('La cantidad debe ser mayor que cero.');
  }

  const timestamp = nowIso();
  const modifiersSnapshot = normalizeModifiersSnapshot(options.modifiers_snapshot || []);
  const nextOptions = {
    ...options,
    notes: options.notes || '',
    modifiers_snapshot: modifiersSnapshot,
  };
  const existingLines = await listOrderLines(order.local_id, scopedRestaurantId);
  const nextLineKey = stableLineOptionsKey(nextOptions);
  const existingLine = existingLines.find((line) =>
    normalizeId(line.product_id) === normalizeId(product.id) &&
    stableLineOptionsKey({
      notes: line.notes || '',
      modifiers_snapshot: line.modifiers_snapshot || [],
    }) === nextLineKey
  );

  if (existingLine) {
    const nextQuantity = Number(existingLine.quantity || 0) + amount;
    const nextLine = {
      ...existingLine,
      quantity: nextQuantity,
      total_amount: lineTotal(nextQuantity, existingLine.unit_price),
      updated_at_local: timestamp,
    };
    await saveRequiredRecord('order_lines', nextLine);
    await enqueueOrderAction(scopedRestaurantId, {
      device_id: getOrCreateDeviceId(),
      entity_type: 'order_line',
      entity_local_id: nextLine.local_id,
      entity_server_id: nextLine.server_id,
      action_type: 'update_order_line',
      payload: nextLine,
    });
    await recalculateLocalOrderTotals(scopedRestaurantId, order.local_id);
    return nextLine;
  }

  const unitPrice = productUnitPrice(product, options);
  const totalAmount = lineTotal(amount, unitPrice);
  const taxRate = productTaxRate(product);
  const line = {
    local_id: createLocalId('local_line'),
    server_id: null,
    restaurant_id: scopedRestaurantId,
    order_local_id: order.local_id,
    order_server_id: null,
    product_id: normalizeId(product.id),
    product_name_snapshot: productName(product),
    product_price_snapshot: normalizeMoney(product.price),
    quantity: amount,
    unit_price: unitPrice,
    total_amount: totalAmount,
    tax_rate_snapshot: taxRate,
    modifiers_snapshot: modifiersSnapshot,
    notes: nextOptions.notes,
    status: 'draft_local',
    sync_status: ORDER_SYNC_STATUS,
    kitchen_state: 'PENDIENTE',
    created_at_local: timestamp,
    updated_at_local: timestamp,
  };

  await saveRequiredRecord('order_lines', line);
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'order_line',
    entity_local_id: line.local_id,
    entity_server_id: line.server_id,
    action_type: 'create_order_line',
    payload: line,
  });
  await recalculateLocalOrderTotals(scopedRestaurantId, order.local_id);

  return line;
}

export async function updateLocalOrderLine(restaurantId, lineLocalId, updates = {}) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const line = await getRecord('order_lines', lineLocalId);
  assertSameRestaurant(line, scopedRestaurantId, 'La linea local');

  const nextQuantity = updates.quantity !== undefined ? Number(updates.quantity) : Number(line.quantity || 0);
  if (!Number.isFinite(nextQuantity) || nextQuantity <= 0) {
    throw new Error('La cantidad debe ser mayor que cero.');
  }

  const nextLine = {
    ...line,
    ...updates,
    quantity: nextQuantity,
    total_amount: lineTotal(nextQuantity, updates.unit_price ?? line.unit_price),
    updated_at_local: nowIso(),
  };

  await saveRequiredRecord('order_lines', nextLine);
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'order_line',
    entity_local_id: nextLine.local_id,
    entity_server_id: nextLine.server_id,
    action_type: 'update_order_line',
    payload: nextLine,
  });
  await recalculateLocalOrderTotals(scopedRestaurantId, nextLine.order_local_id);

  return nextLine;
}

export async function removeLocalOrderLine(restaurantId, lineLocalId) {
  assertCurrentDeviceCanCreateOfflineActions();
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const line = await getRecord('order_lines', lineLocalId);
  assertSameRestaurant(line, scopedRestaurantId, 'La linea local');

  await deleteRecord('order_lines', line.local_id);
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'order_line',
    entity_local_id: line.local_id,
    entity_server_id: line.server_id,
    action_type: 'delete_order_line',
    payload: line,
  });
  await recalculateLocalOrderTotals(scopedRestaurantId, line.order_local_id);
  return true;
}

export async function recalculateLocalOrderTotals(restaurantId, orderLocalId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const order = await getRecord('orders', orderLocalId);
  assertSameRestaurant(order, scopedRestaurantId, 'El pedido local');

  const lines = await listOrderLines(order.local_id, scopedRestaurantId);
  const totalAmount = normalizeMoney(lines.reduce((total, line) => total + normalizeMoney(line.total_amount), 0));
  const taxAmount = normalizeMoney(lines.reduce(
    (total, line) => total + taxFromGross(line.total_amount, line.tax_rate_snapshot),
    0
  ));

  const nextOrder = {
    ...order,
    total_amount: totalAmount,
    tax_amount: taxAmount,
    updated_at_local: nowIso(),
  };
  await saveRequiredRecord('orders', nextOrder);
  return nextOrder;
}

export async function confirmLocalOrder(restaurantId, orderLocalId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const order = await recalculateLocalOrderTotals(scopedRestaurantId, orderLocalId);
  if (order.total_amount <= 0) {
    throw new Error('No se puede confirmar una comanda local vacia.');
  }

  const nextOrder = {
    ...order,
    status: 'confirmed_local',
    sync_status: ORDER_SYNC_STATUS,
    kitchen_state: order.kitchen_state || 'PENDIENTE',
    updated_at_local: nowIso(),
  };
  await saveRequiredRecord('orders', nextOrder);
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'order',
    entity_local_id: nextOrder.local_id,
    action_type: 'confirm_order',
    payload: nextOrder,
  });
  return nextOrder;
}

export async function updateLocalOrderCustomerData(restaurantId, orderLocalId, customerData = {}) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const order = await getRecord('orders', orderLocalId);
  assertSameRestaurant(order, scopedRestaurantId, 'El pedido local');

  const nextOrder = {
    ...order,
    frequent_customer_id: customerData.frequent_customer_id || null,
    customer_name: customerData.customer_name || null,
    customer_phone: customerData.customer_phone || null,
    customer_email: customerData.customer_email || null,
    customer_address: customerData.customer_address || null,
    customer_piso: customerData.customer_piso || null,
    customer_cod_postal: customerData.customer_cod_postal || null,
    customer_additional_info: customerData.customer_additional_info || null,
    customer_pay_with_card: customerData.customer_pay_with_card ?? null,
    customer_latitude: customerData.customer_latitude || null,
    customer_longitude: customerData.customer_longitude || null,
    updated_at_local: nowIso(),
  };

  await saveRequiredRecord('orders', nextOrder);
  return nextOrder;
}

export async function cancelLocalOrder(restaurantId, orderLocalId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const order = await getRecord('orders', orderLocalId);
  assertSameRestaurant(order, scopedRestaurantId, 'El pedido local');

  const nextOrder = {
    ...order,
    status: 'cancelled_local',
    sync_status: ORDER_SYNC_STATUS,
    updated_at_local: nowIso(),
  };
  await saveRequiredRecord('orders', nextOrder);
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'order',
    entity_local_id: nextOrder.local_id,
    action_type: 'cancel_order',
    payload: nextOrder,
  });
  return nextOrder;
}

export async function updateLocalOrderLineKitchenState(restaurantId, lineLocalId, state) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const line = await getRecord('order_lines', lineLocalId);
  assertSameRestaurant(line, scopedRestaurantId, 'La linea local');

  const nextLine = {
    ...line,
    kitchen_state: normalizeVisibleKitchenState(state, line.kitchen_state || 'PENDIENTE'),
    sync_status: ORDER_SYNC_STATUS,
    updated_at_local: nowIso(),
  };
  await saveRequiredRecord('order_lines', nextLine);

  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'order_line',
    entity_local_id: nextLine.local_id,
    entity_server_id: nextLine.server_id,
    action_type: 'update_order_line_status',
    payload: {
      ...nextLine,
      state,
    },
  });

  return nextLine;
}

export async function updateLocalOrderKitchenState(restaurantId, orderLocalId, state) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const order = await getRecord('orders', orderLocalId);
  assertSameRestaurant(order, scopedRestaurantId, 'El pedido local');

  const timestamp = nowIso();
  await saveRequiredRecord('orders', {
    ...order,
    kitchen_state: normalizeVisibleKitchenState(state, order.kitchen_state || 'PENDIENTE'),
    sync_status: ORDER_SYNC_STATUS,
    updated_at_local: timestamp,
  });

  const lines = await listOrderLines(order.local_id, scopedRestaurantId);
  await Promise.all(lines.map((line) => saveRequiredRecord('order_lines', {
    ...line,
    kitchen_state: normalizeVisibleKitchenState(state, line.kitchen_state || order.kitchen_state || 'PENDIENTE'),
    sync_status: ORDER_SYNC_STATUS,
    updated_at_local: timestamp,
  })));

  await enqueueOrderAction(scopedRestaurantId, {
    device_id: getOrCreateDeviceId(),
    entity_type: 'order',
    entity_local_id: order.local_id,
    entity_server_id: order.server_id,
    action_type: 'update_order_status',
    payload: {
      ...order,
      state,
      updated_at_local: timestamp,
    },
  });

  return true;
}

export async function createLocalPaymentPlaceholder(restaurantId, paymentTarget, payData = {}, context = {}) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const table = await getScopedTable(scopedRestaurantId, paymentTarget.table_id);
  if (!table) {
    throw new Error('La mesa no pertenece al restaurante activo.');
  }

  if (paymentTarget.order_local_id) {
    const order = await getRecord('orders', paymentTarget.order_local_id);
    assertSameRestaurant(order, scopedRestaurantId, 'El pedido local');
  }

  const timestamp = nowIso();
  const allowedMethods = ['cash', 'card', 'bizum'];
  const requestedMethod = allowedMethods.includes(payData.method) ? payData.method : 'cash';
  const method = `${requestedMethod}_pending_sync`;
  const payment = {
    local_id: createLocalId('local_payment'),
    server_id: null,
    service_local_id: normalizeId(paymentTarget.service_local_id || context.service_local_id),
    service_server_id: normalizeId(paymentTarget.service_server_id || context.service_server_id),
    order_local_id: normalizeId(paymentTarget.order_local_id),
    order_server_id: normalizeId(paymentTarget.order_server_id),
    restaurant_id: scopedRestaurantId,
    table_id: normalizeId(paymentTarget.table_id),
    payment_method: method,
    method: requestedMethod,
    amount: normalizeMoney(paymentTarget.amount),
    amount_tendered: normalizeMoney(payData.amount_tendered ?? paymentTarget.amount),
    change: normalizeMoney(payData.change),
    status: 'pending_sync',
    sync_status: ORDER_SYNC_STATUS,
    created_at_local: timestamp,
    updated_at_local: timestamp,
    created_by_device_id: getOrCreateDeviceId(),
    created_by_user_id: getUserId(context),
  };

  await saveRequiredRecord('payments', payment);

  if (payment.order_local_id) {
    const order = await getRecord('orders', payment.order_local_id);
    await saveRequiredRecord('orders', {
      ...order,
      status: 'paid_local',
      sync_status: ORDER_SYNC_STATUS,
      updated_at_local: timestamp,
    });
  }

  await enqueueOrderAction(scopedRestaurantId, {
    device_id: payment.created_by_device_id,
    entity_type: 'payment',
    entity_local_id: payment.local_id,
    entity_server_id: payment.order_server_id,
    action_type: 'create_payment_placeholder',
    payload: payment,
  });

  return payment;
}

async function createLocalTablePaymentMarker(restaurantId, tableId, payData = {}, context = {}) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const table = await getScopedTable(scopedRestaurantId, tableId);
  if (!table) {
    throw new Error('La mesa no pertenece al restaurante activo.');
  }

  const timestamp = nowIso();
  const allowedMethods = ['cash', 'card', 'bizum'];
  const requestedMethod = allowedMethods.includes(payData.method) ? payData.method : 'cash';
  const payment = {
    local_id: createLocalId('local_payment'),
    server_id: null,
    service_local_id: normalizeId(context.service_local_id),
    service_server_id: normalizeId(context.service_server_id),
    order_local_id: null,
    order_server_id: null,
    restaurant_id: scopedRestaurantId,
    table_id: normalizeId(tableId),
    payment_method: `${requestedMethod}_pending_sync`,
    method: requestedMethod,
    amount: 0,
    amount_tendered: 0,
    change: 0,
    status: 'pending_sync',
    sync_status: ORDER_SYNC_STATUS,
    created_at_local: timestamp,
    updated_at_local: timestamp,
    created_by_device_id: getOrCreateDeviceId(),
    created_by_user_id: getUserId(context),
    closes_whole_table: true,
  };

  await saveRequiredRecord('payments', payment);
  await enqueueOrderAction(scopedRestaurantId, {
    device_id: payment.created_by_device_id,
    entity_type: 'payment',
    entity_local_id: payment.local_id,
    entity_server_id: null,
    action_type: 'create_payment_placeholder',
    payload: payment,
  });

  return payment;
}

export async function createLocalPaymentPlaceholdersForBlocks(restaurantId, blocks = [], payData = {}, context = {}) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const payableBlocks = blocks.filter((block) => Number(block?.subtotal || 0) > 0);
  const payments = [];

  for (const block of payableBlocks) {
    payments.push(await createLocalPaymentPlaceholder(scopedRestaurantId, {
      table_id: block.table_id || context.table_id,
      service_local_id: block.service_local_id || context.service_local_id,
      service_server_id: block.service_server_id || context.service_server_id,
      order_local_id: block.local_order_id || (block.source === 'offline' ? block.block_id : null),
        order_server_id: block.order_server_id || block.order_id || block.server_id || null,
      amount: block.subtotal,
    }, payData, context));
  }

  if (context.close_table_on_payment && context.table_id) {
    payments.push(await createLocalTablePaymentMarker(scopedRestaurantId, context.table_id, payData, context));
    if (context.service_local_id) {
      await closeLocalService(scopedRestaurantId, context.service_local_id);
    }
  }

  return payments;
}

export async function listLocalPendingPayments(restaurantId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const payments = await listRestaurantRecords('payments', scopedRestaurantId);
  return payments.filter((payment) => !['completed', 'cancelled'].includes(payment.status));
}

export async function listLocalPendingPaymentsForTable(restaurantId, tableId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const payments = await listLocalPendingPayments(scopedRestaurantId);
  return payments.filter((payment) =>
    normalizeId(payment.table_id) === normalizeId(tableId)
  );
}

export async function listLocalOpenOrders(restaurantId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const orders = await listRestaurantRecords('orders', scopedRestaurantId);
  return orders.filter((order) => OPEN_ORDER_STATUSES.includes(order.status));
}

export async function getLocalOrderWithLines(restaurantId, orderLocalId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const order = await getRecord('orders', orderLocalId);
  assertSameRestaurant(order, scopedRestaurantId, 'El pedido local');

  const lines = await listOrderLines(order.local_id, scopedRestaurantId);
  return buildVisibleLocalOrder(order, lines);
}

export async function listLocalOrdersForTable(restaurantId, tableId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const orders = await listRestaurantRecords('orders', scopedRestaurantId);
  return orders.filter((order) =>
    normalizeId(order.table_id) === normalizeId(tableId) && OPEN_ORDER_STATUSES.includes(order.status)
  );
}

export async function listLocalOrdersByDateRange(restaurantId, fromDate = null, toDate = null) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const orders = await listRestaurantRecords('orders', scopedRestaurantId);

  const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
  const toTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;

  return orders.filter((order) => {
    if (order.status === 'cancelled_local') return false;
    const createdAt = new Date(order.created_at_local || order.updated_at_local || 0).getTime();
    if (!Number.isFinite(createdAt)) return false;
    if (fromTs !== null && createdAt < fromTs) return false;
    if (toTs !== null && createdAt > toTs) return false;
    return true;
  }).sort((a, b) => new Date(b.created_at_local || 0) - new Date(a.created_at_local || 0));
}

export async function getLocalUnsyncedOrderCount(restaurantId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const orders = await listRestaurantRecords('orders', scopedRestaurantId);
  return orders.filter((order) => order.sync_status !== 'synced').length;
}

export async function markLocalOrderSynced(restaurantId, orderLocalId, serverId = null) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const order = await getRecord('orders', orderLocalId);
  assertSameRestaurant(order, scopedRestaurantId, 'El pedido local');

  const timestamp = nowIso();
  let serviceServerId = order.service_server_id || null;
  await saveRequiredRecord('orders', {
    ...order,
    server_id: serverId || order.server_id || null,
    service_server_id: serviceServerId,
    status: 'synced',
    sync_status: 'synced',
    synced_at: timestamp,
    updated_at_local: timestamp,
  });

  const lines = await listOrderLines(order.local_id, scopedRestaurantId);
  await Promise.all(lines.map((line) => saveRequiredRecord('order_lines', {
    ...line,
    order_server_id: serverId || line.order_server_id || null,
    status: 'synced',
    sync_status: 'synced',
    synced_at: timestamp,
    updated_at_local: timestamp,
  })));

  if (order.service_local_id) {
    const service = await getRecord('open_services', order.service_local_id);
    if (service && String(service.restaurant_id || '') === scopedRestaurantId) {
      serviceServerId = service.server_id || serviceServerId;
      await saveRequiredRecord('open_services', {
        ...service,
        status: 'synced',
        sync_status: 'synced',
        synced_at: timestamp,
        updated_at_local: timestamp,
      });

      await saveRequiredRecord('orders', {
        ...order,
        server_id: serverId || order.server_id || null,
        service_server_id: serviceServerId,
        status: 'synced',
        sync_status: 'synced',
        synced_at: timestamp,
        updated_at_local: timestamp,
      });
    }
  }

  return true;
}

export async function markLocalOrderLineSynced(restaurantId, lineLocalId, serverId = null, orderServerId = null) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const line = await getRecord('order_lines', lineLocalId);
  assertSameRestaurant(line, scopedRestaurantId, 'La linea local');

  const timestamp = nowIso();
  await saveRequiredRecord('order_lines', {
    ...line,
    server_id: serverId || line.server_id || null,
    order_server_id: orderServerId || line.order_server_id || null,
    status: 'synced',
    sync_status: 'synced',
    synced_at: timestamp,
    updated_at_local: timestamp,
  });

  return true;
}

export async function markLocalServiceSynced(restaurantId, serviceLocalId, serverId = null) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const service = await getRecord('open_services', serviceLocalId);
  assertSameRestaurant(service, scopedRestaurantId, 'El servicio local');

  const timestamp = nowIso();
  await saveRequiredRecord('open_services', {
    ...service,
    server_id: serverId || service.server_id || null,
    sync_status: 'synced',
    synced_at: timestamp,
    updated_at_local: timestamp,
  });

  return true;
}

export async function markLocalPaymentSynced(restaurantId, paymentLocalId, serverId = null) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const payment = await getRecord('payments', paymentLocalId);
  assertSameRestaurant(payment, scopedRestaurantId, 'El pago local');

  const timestamp = nowIso();
  await saveRequiredRecord('payments', {
    ...payment,
    server_id: serverId || payment.server_id || null,
    status: 'synced',
    sync_status: 'synced',
    synced_at: timestamp,
    updated_at_local: timestamp,
  });

  return true;
}

export async function getLocalPosBlocksForTable(restaurantId, tableId) {
  const scopedRestaurantId = assertRestaurantId(restaurantId);
  const orders = await listLocalOrdersForTable(scopedRestaurantId, tableId);

  return Promise.all(orders.map(async (order) => {
    const lines = await listOrderLines(order.local_id, scopedRestaurantId);
    const visibleOrder = buildVisibleLocalOrder(order, lines);
    return {
      block_id: order.local_id,
      order_id: null,
      status: order.status === 'draft_local' ? 'draft' : 'confirmed',
      source: 'offline',
      title: order.status === 'draft_local' ? 'Pedido local' : 'Comanda local',
      created_at: order.created_at_local,
      updated_at: order.updated_at_local,
      state: visibleOrder.state,
      subtotal: normalizeMoney(order.total_amount),
      editable: order.status === 'draft_local',
      local_order_id: order.local_id,
      order_server_id: order.server_id || null,
      service_local_id: order.service_local_id || null,
      service_server_id: order.service_server_id || null,
      sync_status: order.sync_status,
      created_by_name: order.created_by_name || null,
      items: lines.map((line) => ({
        id: line.local_id,
        server_id: line.server_id || null,
        plate_id: line.product_id,
        category_id: line.category_id || line.product_category_id || null,
        amount: line.quantity,
        plate_name: line.product_name_snapshot,
        comment: line.notes,
        state: normalizeVisibleKitchenState(line.kitchen_state || line.status || order.kitchen_state || visibleOrder.state, visibleOrder.state),
        unit_price: line.unit_price,
        tax_rate_snapshot: line.tax_rate_snapshot,
        steps_selected: line.modifiers_snapshot,
        local_line_id: line.local_id,
        sync_status: line.sync_status,
      })),
    };
  }));
}

export async function getOfflineOrderDiagnostics(restaurantId) {
  if (!restaurantId) {
    return {
      open_services: 0,
      orders: 0,
      unsynced_orders: 0,
      pending_actions: 0,
      last_local_order_created_at: null,
      last_offline_error: null,
    };
  }

  try {
    const scopedRestaurantId = String(restaurantId);
    const [services, orders, pendingActions] = await Promise.all([
      listRestaurantRecords('open_services', scopedRestaurantId),
      listRestaurantRecords('orders', scopedRestaurantId),
      listPendingActions(scopedRestaurantId),
    ]);
    const openServices = services.filter((service) => OPEN_SERVICE_STATUSES.includes(service.status));
    const unsyncedOrders = orders.filter((order) => order.sync_status !== 'synced');
    const lastOrder = [...orders].sort((a, b) =>
      new Date(b.created_at_local || 0) - new Date(a.created_at_local || 0)
    )[0];
    const failedAction = pendingActions.find((action) => action.last_error);

    return {
      open_services: openServices.length,
      orders: orders.length,
      unsynced_orders: unsyncedOrders.length,
      pending_actions: await getPendingActionCount(scopedRestaurantId),
      last_local_order_created_at: lastOrder?.created_at_local || null,
      last_offline_error: failedAction?.last_error || null,
    };
  } catch (error) {
    await addSyncLog({
      level: 'error',
      message: 'Offline order diagnostics failed.',
      entity_type: 'offline_orders',
      entity_id: restaurantId,
      details: error?.message || String(error),
    });
    return {
      open_services: 0,
      orders: 0,
      unsynced_orders: 0,
      pending_actions: 0,
      last_local_order_created_at: null,
      last_offline_error: error?.message || String(error),
    };
  }
}
